import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faLightbulb, faTasks } from '@fortawesome/free-solid-svg-icons';

const ChatSidebar = forwardRef(({
  messages = [],
  onSend,
  onEvaluate,
  selectedPlotItem,
  isVisible = true,
  showTitle = true,
  aiCalls = 0 // New prop for AI calls count
}, ref) => {
  const [inputValue, setInputValue] = useState('');
  const [isContextActive, setIsContextActive] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    setChatInputValue: (value) => {
      setInputValue(value);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }));

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() && onSend) {
      onSend(inputValue.trim(), isContextActive);
      setInputValue('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="d-flex flex-column h-100 bg-white border-start">
      {/* Header with AI calls count */}
      <div className="p-3 border-bottom bg-light">
        <div className="d-flex justify-content-between align-items-center">
          {showTitle && (
            <h5 className="mb-0">AI Assistant</h5>
          )}
          {/* AI calls counter - small text on right side */}
          <small className="text-muted">
            {aiCalls} AI calls
          </small>
        </div>
      </div>

      {/* Selected plot item context */}
      {selectedPlotItem && (
        <div className="selected-plot-item p-2 border-bottom bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Selected: Beat #{selectedPlotItem.number || 'N/A'}
            </small>
            <button
              className="btn btn-link btn-sm p-0"
              onClick={() => setIsContextActive(!isContextActive)}
              title={isContextActive ? "Don't include context" : "Include context"}
            >
              <FontAwesomeIcon 
                icon={faLightbulb} 
                className={isContextActive ? 'text-warning' : 'text-muted'} 
              />
            </button>
          </div>
          {isContextActive && (
            <div className="mt-1">
              <small className="text-truncate d-block">
                {selectedPlotItem.content?.substring(0, 100)}...
              </small>
            </div>
          )}
        </div>
      )}

      {/* Messages area */}
      <div className="flex-grow-1 p-3 chat-messages-container" style={{ overflowY: 'auto' }}>
        {messages.length === 0 ? (
          <div className="text-center text-muted">
            <p>Start a conversation with the AI assistant!</p>
          </div>
        ) : (
          messages.map((message, index) => (
            <div
              key={message.id || index}
              className={`chat-message mb-3 ${
                message.isUser || message.isAi === false 
                  ? 'ms-auto bg-primary text-white' 
                  : 'me-auto bg-light'
              } p-2 rounded`}
              style={{ maxWidth: '80%' }}
            >
              {message.content || message.message || message.text}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="p-3 border-top">
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <textarea
              ref={inputRef}
              className="form-control"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about your screenplay..."
              rows={2}
              style={{ resize: 'none' }}
            />
            <button 
              className="btn btn-primary" 
              type="submit"
              disabled={!inputValue.trim()}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>
        </form>
        
        {/* Evaluate button */}
        {/* {onEvaluate && (
          <div className="mt-2">
            <button 
              className="btn btn-outline-secondary btn-sm w-100"
              onClick={onEvaluate}
            >
              <FontAwesomeIcon icon={faTasks} className="me-1" />
              Evaluate Scenee
            </button>
          </div>
        )} */}
      </div>
    </div>
  );
});

ChatSidebar.displayName = 'ChatSidebar';

export default ChatSidebar;